import {
    ALL_TRIPS_TRIP_ID,
    TTA_ALL_TRIPS_OBSERVED_TRIP_ID,
} from 'src/routes/trip-analyzer/data/trip-analyzer.constants';
import { isTimestampInWindow } from 'src/routes/trip-analyzer/data/timeWindow';

/**
 * @param {Array<string>|string|null|undefined} value
 * @returns {Array<string>}
 */
export const normalizeTripIds = (value) => {
    if (Array.isArray(value)) {
        return value.map((id) => String(id)).filter(Boolean);
    }
    if (value == null || value === '') {
        return [];
    }
    return [String(value)];
};

/**
 * @param {Array<string>|string|null|undefined} ids
 * @returns {boolean}
 */
export const isAllTrips = (ids) => {
    const list = normalizeTripIds(ids);
    return list.length === 1 && list[0] === ALL_TRIPS_TRIP_ID;
};

/**
 * @param {Array<string>|string|null|undefined} ids
 * @returns {boolean}
 */
export const hasTripSelection = (ids) => normalizeTripIds(ids).length > 0;

/**
 * Date, route, and direction are required before corridor catalog or timeline fetches.
 *
 * @param {Object} query
 * @returns {boolean}
 */
export const isCorridorQueryComplete = (query) => Boolean(
    query?.serviceDate
    && query?.routeKey
    && query?.directionId !== ''
    && query?.directionId != null,
);

/**
 * Compare sets lock route/direction once trips are added; service date stays editable
 * so users can search additional dates on the same corridor.
 *
 * @param {Object} query
 * @returns {boolean}
 */
export const shouldPreserveCompareCorridorOnDateChange = (query) => Boolean(
    query?.compareEnabled
    && (query.compareTrips?.length ?? 0) > 0
    && query?.routeKey
    && query?.directionId !== ''
    && query?.directionId != null,
);

/**
 * True when the query is complete enough to request timeline data.
 * Default "__all__" only counts after the corridor cascade is filled in.
 *
 * @param {Object} query
 * @returns {boolean}
 */
/**
 * Whether the compare preview panel can fetch a single search trip.
 *
 * @param {Object} query
 * @returns {boolean}
 */
export const isComparePreviewFetchReady = (query) => {
    if (!query.compareEnabled || !isCorridorQueryComplete(query)) {
        return false;
    }

    const tripIds = normalizeTripIds(query.observedTripIds);
    const previewTripId = tripIds.length === 1 ? tripIds[0] : null;

    return Boolean(previewTripId) && previewTripId !== ALL_TRIPS_TRIP_ID;
};

/**
 * Whether the main comparison timeline can fetch merged trips.
 *
 * @param {Object} query
 * @returns {boolean}
 */
export const isTimelineFetchReady = (query) => {
    if (!isCorridorQueryComplete(query)) {
        return false;
    }

    if (query.compareEnabled) {
        return (query.compareTrips?.length ?? 0) > 0;
    }

    return hasTripSelection(normalizeTripIds(query.observedTripIds));
};

/**
 * Setup-step completion for the guided timeline empty state.
 *
 * @param {Object} query
 * @returns {{ date: boolean, route: boolean, direction: boolean, trip: boolean, hasTripA: boolean, hasTripB: boolean }}
 */
export const buildTimelineSetupProgress = (query, { isPreview = false } = {}) => {
    const tripIds = normalizeTripIds(query.observedTripIds);
    const hasTripA = tripIds.length === 1 && tripIds[0] !== ALL_TRIPS_TRIP_ID;
    const hasTripB = (query.compareTrips?.length ?? 0) > 0
        || Boolean(query.compareObservedTripId);

    const tripComplete = isPreview
        ? isComparePreviewFetchReady(query)
        : query.compareEnabled
            ? isComparePreviewFetchReady(query) || (query.compareTrips?.length ?? 0) > 0
            : isTimelineFetchReady(query);

    return {
        date: Boolean(query.serviceDate),
        route: Boolean(query.routeKey),
        direction: isCorridorQueryComplete(query),
        trip: tripComplete,
        hasTripA,
        hasTripB,
    };
};

/**
 * Compare-mode onboarding steps for the guided timeline empty state.
 *
 * @param {Object} query
 * @returns {{ corridor: boolean, firstTrip: boolean, moreTrips: boolean }}
 */
export const buildCompareSetupProgress = (query) => ({
    corridor: isCorridorQueryComplete(query),
    firstTrip: (query?.compareTrips?.length ?? 0) > 0,
    moreTrips: (query?.compareTrips?.length ?? 0) > 1,
});

/**
 * @param {Record<string, boolean>} [selectedOptions]
 * @returns {Array<string>}
 */
export const selectionMapToTripIds = (selectedOptions = {}) =>
    Object.keys(selectedOptions).filter((key) => selectedOptions[key]);

/**
 * @param {Array<string>|string|null|undefined} ids
 * @returns {Record<string, boolean>}
 */
export const tripIdsToSelectionMap = (ids) => {
    const map = {};
    normalizeTripIds(ids).forEach((id) => {
        map[id] = true;
    });
    return map;
};

/**
 * Enforce All trips exclusivity when translating a multiselect map to ids.
 * @param {Record<string, boolean>} selectedOptions
 * @param {Array<string>|string|null|undefined} previousIds
 * @returns {Array<string>}
 */
export const resolveTripSelectionFromMap = (selectedOptions, previousIds = []) => {
    const nextIds = selectionMapToTripIds(selectedOptions);
    const prevIds = normalizeTripIds(previousIds);
    const prevMap = tripIdsToSelectionMap(prevIds);
    const allSelected = Boolean(selectedOptions?.[ALL_TRIPS_TRIP_ID]);
    const allWasSelected = Boolean(prevMap[ALL_TRIPS_TRIP_ID]);
    const addedAll = allSelected && !allWasSelected;
    const addedSpecific = nextIds.filter((id) => id !== ALL_TRIPS_TRIP_ID && !prevMap[id]);

    if (addedAll) {
        return [ALL_TRIPS_TRIP_ID];
    }
    if (addedSpecific.length && isAllTrips(prevIds)) {
        return addedSpecific;
    }
    if (allSelected && nextIds.length > 1) {
        return nextIds.filter((id) => id !== ALL_TRIPS_TRIP_ID);
    }
    return nextIds;
};

/**
 * @param {Array<string>|string|null|undefined} observedTripIds
 * @returns {string|null}
 */
export const getTimelineApiTripId = (observedTripIds) => {
    const ids = normalizeTripIds(observedTripIds);
    if (!hasTripSelection(ids)) {
        return null;
    }
    // Always fetch the full corridor once per cascade; filter client-side for
    // single-trip, multi-trip, and all-trips selections.
    return ALL_TRIPS_TRIP_ID;
};

/**
 * Returns true when a timeline column should be visually blocked for the
 * selected jurisdiction partner keys (client-side filter only).
 *
 * @param {Object} column
 * @param {Array<string>} partnerKeys
 * @returns {boolean}
 */
export const isColumnBlockedByPartner = (column, partnerKeys = []) => {
    if (!partnerKeys?.length) {
        return false;
    }

    const partner = String(column?.partner || '').trim().toLowerCase();
    if (!partner) {
        return false;
    }

    return !partnerKeys.some(
        (key) => String(key).trim().toLowerCase() === partner,
    );
};

/**
 * @param {Array<Object>} trips
 * @param {string|null|undefined} windowStart
 * @param {string|null|undefined} windowEnd
 * @returns {Array<Object>}
 */
/**
 * @param {Array<Object>} trips
 * @param {Array<string>|string|null|undefined} observedTripIds
 * @returns {Array<Object>}
 */
export const filterTripsBySelection = (trips = [], observedTripIds = []) => {
    const ids = normalizeTripIds(observedTripIds);
    if (!ids.length) {
        return [];
    }
    if (isAllTrips(ids)) {
        return trips;
    }

    const selected = new Set(ids);
    return trips.filter((trip) => selected.has(String(trip.observed_trip_id)));
};

export const filterTripsByTimeWindow = (trips = [], windowStart, windowEnd) => {
    if (!windowStart && !windowEnd) {
        return trips;
    }

    return trips.filter((trip) => isTimestampInWindow(trip.local_start_ts, windowStart, windowEnd));
};

/**
 * @param {string|null|undefined} observedTripId
 * @param {boolean} compareEnabled
 * @returns {string|null}
 */
export const resolveObservedTripIdForTimeline = (observedTripIds, compareEnabled = false) => {
    const ids = normalizeTripIds(observedTripIds);
    if (compareEnabled) {
        if (!hasTripSelection(ids) || isAllTrips(ids)) {
            return null;
        }
        return ids[0];
    }

    return getTimelineApiTripId(ids);
};

/**
 * Map internal trip ids to the Nest TTA timeline API contract.
 *
 * @param {string|null|undefined} observedTripId
 * @returns {string}
 */
export const toTimelineApiObservedTripId = (observedTripId) => {
    if (!observedTripId || observedTripId === ALL_TRIPS_TRIP_ID) {
        return TTA_ALL_TRIPS_OBSERVED_TRIP_ID;
    }

    return observedTripId;
};
